// v4.2.1 — ContentReader (lecteur immersif universel, feuilletage livre numérique).
// Étapes 5-6 de la maquette : pages de journal en images feuilletées comme un
// livre (react-pageflip), vignettes réelles, slider 'Page x/y', toolbar
// Sommaire / Plein écran / Recherche / Paramètres.
// Mode data-driven : si toutes les pages ont une image_url → flipbook ;
// sinon fallback lecture texte page-par-page. Aucune connaissance du média.

import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import HTMLFlipBook from "react-pageflip";
import PageZoomOverlay from "@/components/content/PageZoomOverlay";
import {
  ArrowLeft, Share2, Bookmark, List, Maximize, Search, Settings, X,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const FONT_SIZES = [13, 15, 17.5];

export default function ContentReader({ resourceId, onClose, initialPage = null }) {
  const [state, setState] = useState({ loading: true, page: null });
  const [current, setCurrent] = useState(1);
  const [overlay, setOverlay] = useState(null); // 'toc' | 'search' | 'settings'
  const [fontIdx, setFontIdx] = useState(1);
  const [query, setQuery] = useState("");
  const [zoomOpen, setZoomOpen] = useState(false);
  const containerRef = useRef(null);
  const bookRef = useRef(null);
  const touchStart = useRef(null);
  const lastTap = useRef({ time: 0, x: 0, y: 0 });

  const loadPage = useCallback(
    async (pageNumber) => {
      const token = localStorage.getItem("civ_user_token");
      if (!token) return;
      try {
        setState((s) => ({ ...s, loading: true }));
        const url = pageNumber
          ? `${BACKEND_URL}/api/v3/content/${resourceId}/read?page=${pageNumber}`
          : `${BACKEND_URL}/api/v3/content/${resourceId}/read`;
        const r = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
        setState({ loading: false, page: r.data });
        setCurrent(r.data?.page || 1);
        setOverlay(null);
      } catch (e) {
        setState({ loading: false, page: null, error: e?.response?.data?.error || "read_error" });
        toast.error(e?.response?.data?.error || "Lecture impossible");
      }
    },
    [resourceId],
  );

  useEffect(() => { loadPage(initialPage); }, [loadPage, initialPage]);

  async function saveProgress(n) {
    const token = localStorage.getItem("civ_user_token");
    if (!token) return;
    try {
      await axios.post(
        `${BACKEND_URL}/api/v3/content/${resourceId}/progress`,
        { page: n },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch (_) {}
  }

  function toggleFullscreen() {
    const el = containerRef.current;
    if (!document.fullscreenElement && el?.requestFullscreen) el.requestFullscreen().catch(() => {});
    else if (document.exitFullscreen) document.exitFullscreen().catch(() => {});
  }

  async function handleShare() {
    const p = state.page;
    const text = `${p?.title} — page ${current}`;
    try {
      if (navigator.share) await navigator.share({ title: p?.title, text });
      else { await navigator.clipboard.writeText(text); toast.success("Copié"); }
    } catch (_) {}
  }

  if (!resourceId) return null;
  if (state.loading && !state.page) {
    return (
      <div className="min-h-screen bg-[#0a0a0d] flex items-center justify-center text-amber-300/70" data-testid="content-reader-loading">
        Ouverture de la lecture…
      </div>
    );
  }
  if (state.error === "entitlement_required") {
    return (
      <div className="min-h-screen bg-[#0a0a0d] flex items-center justify-center p-6">
        <div className="p-6 rounded-2xl border border-amber-500/30 bg-amber-500/5 text-amber-200 text-sm" data-testid="content-reader-locked">
          Ce contenu n'est pas déverrouillé. Achetez-le pour continuer.
        </div>
      </div>
    );
  }
  const p = state.page;
  if (!p) return <div className="min-h-screen bg-[#0a0a0d] flex items-center justify-center text-white/60">Contenu indisponible</div>;

  const toc = p.toc || [];
  const flipMode = toc.length > 0 && toc.every((t) => t.image_url);
  const totalPages = p.total_pages || toc.length;
  const results = query
    ? toc.filter((t) => `${t.headline} ${t.section}`.toLowerCase().includes(query.toLowerCase()))
    : toc;

  function goTo(n) {
    const target = Math.min(Math.max(1, n), totalPages);
    if (flipMode) {
      try { bookRef.current?.pageFlip()?.flip(target - 1); } catch (_) {}
    } else {
      loadPage(target);
    }
    setOverlay(null);
  }

  function onFlip(e) {
    const n = (e?.data ?? 0) + 1;
    setCurrent(n);
    saveProgress(n);
  }

  // v4.3.0 — Double-tap (mobile) / double-clic (desktop) sur la page → zoom.
  function onBookPointerUp(e) {
    const now = Date.now();
    const dt = now - lastTap.current.time;
    const moved = Math.hypot(e.clientX - lastTap.current.x, e.clientY - lastTap.current.y);
    if (dt < 320 && moved < 30) {
      setZoomOpen(true);
      lastTap.current = { time: 0, x: 0, y: 0 };
      return;
    }
    lastTap.current = { time: now, x: e.clientX, y: e.clientY };
  }

  // Swipe uniquement en mode texte (le flipbook gère lui-même le drag)
  function onTouchStart(e) { if (!flipMode) touchStart.current = e.touches[0].clientX; }
  function onTouchEnd(e) {
    if (flipMode || touchStart.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStart.current;
    touchStart.current = null;
    if (state.loading) return;
    if (dx < -60 && current < totalPages) loadPage(current + 1);
    else if (dx > 60 && current > 1) loadPage(current - 1);
  }

  const bw = Math.min((typeof window !== "undefined" ? window.innerWidth : 400) - 56, 380);
  const bh = Math.round(bw * 1.45);

  return (
    <div
      ref={containerRef}
      data-testid={`content-reader-${resourceId}`}
      className="min-h-screen bg-[#0a0a0d] text-white flex flex-col"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <style>{`
        .aya-paper-body { column-gap: 1.6em; text-align: justify; hyphens: auto; }
        @media (min-width: 480px) { .aya-paper-body { column-count: 2; } }
        .aya-paper-body::first-letter { font-size: 2.6em; font-weight: 800; float: left; line-height: 0.85; padding-right: 0.08em; font-family: Georgia, serif; }
        .aya-flipbook { margin: 0 auto; }
        .aya-flip-page { background: #fff; box-shadow: 0 0 18px rgba(0,0,0,0.55); overflow: hidden; }
        .aya-flip-page img { width: 100%; height: 100%; object-fit: cover; object-position: top; display: block; user-select: none; -webkit-user-drag: none; }
      `}</style>

      {/* Header */}
      <div className="sticky top-0 z-20 bg-[#0a0a0d]/95 backdrop-blur border-b border-white/8">
        <div className="max-w-3xl mx-auto flex items-center gap-3 px-4 py-3">
          <button onClick={onClose} data-testid="content-reader-close" className="p-1.5 -ml-1.5 rounded-full hover:bg-white/10 transition-colors">
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[15px] font-bold leading-tight truncate">{p.title}</div>
            <div className="text-[11px] text-white/50">{p.edition_date || ""}</div>
          </div>
          <button onClick={handleShare} className="p-2 rounded-full hover:bg-white/10 transition-colors" data-testid="content-reader-share">
            <Share2 size={18} />
          </button>
          <button className="p-2 rounded-full hover:bg-white/10 transition-colors" data-testid="content-reader-bookmark">
            <Bookmark size={18} />
          </button>
        </div>
      </div>

      {/* Zone de lecture */}
      <div className="flex-1 overflow-y-auto px-4 py-4 flex items-start justify-center">
        {flipMode ? (
          <div
            data-testid="content-reader-flipbook"
            className="select-none"
            onPointerUp={onBookPointerUp}
            onDoubleClick={() => setZoomOpen(true)}
            onTouchStartCapture={(e) => {
              // v4.3.1 — Pincement 2 doigts sur la page : react-pageflip ne gère
              // pas le multi-touch (crash StPageFlip). On intercepte AVANT la lib
              // et on ouvre l'overlay de zoom dédié.
              if (e.touches && e.touches.length >= 2) {
                e.stopPropagation();
                setZoomOpen(true);
              }
            }}
            onTouchMoveCapture={(e) => {
              if (e.touches && e.touches.length >= 2) e.stopPropagation();
            }}
          >
            <HTMLFlipBook
              ref={bookRef}
              width={bw}
              height={bh}
              minWidth={220}
              maxWidth={640}
              minHeight={320}
              maxHeight={980}
              startPage={(p.page || 1) - 1}
              showCover={false}
              usePortrait={true}
              maxShadowOpacity={0.6}
              mobileScrollSupport={false}
              flippingTime={700}
              onFlip={onFlip}
              className="aya-flipbook"
            >
              {toc.map((t) => (
                <div key={t.number} className="aya-flip-page">
                  <img src={t.image_url} alt={`Page ${t.number} — ${t.headline}`} />
                </div>
              ))}
            </HTMLFlipBook>
            <div className="text-center text-[10.5px] text-white/30 mt-2">
              Double-tapez la page pour zoomer · glissez le coin pour tourner
            </div>
          </div>
        ) : (
          <div className="max-w-2xl w-full mx-auto rounded-lg overflow-hidden shadow-2xl bg-[#faf7f0] text-neutral-900">
            <div className="px-5 pt-4 pb-2 border-b-[3px] border-neutral-900 flex items-end justify-between gap-3">
              <div className="text-xl font-black tracking-tight" style={{ fontFamily: "Georgia, serif" }}>{p.title}</div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 pb-0.5 whitespace-nowrap">{p.edition_date || ""}</div>
            </div>
            <div className="bg-neutral-900 text-white text-[11px] uppercase tracking-[0.35em] px-5 py-1.5" data-testid="content-reader-section">
              {p.page_content?.section}
            </div>
            <div className="px-5 sm:px-7 py-5">
              <h2 className="font-bold leading-snug mb-4" style={{ fontFamily: "Georgia, serif", fontSize: `${FONT_SIZES[fontIdx] + 8}px` }} data-testid="content-reader-headline">
                {p.page_content?.headline}
              </h2>
              <div className="aya-paper-body leading-relaxed whitespace-pre-line" style={{ fontFamily: "Georgia, serif", fontSize: `${FONT_SIZES[fontIdx]}px` }} data-testid="content-reader-body">
                {p.page_content?.body}
              </div>
            </div>
            <div className="px-5 pb-3 text-right text-[10px] text-neutral-400 font-mono">{current} / {totalPages}</div>
          </div>
        )}
      </div>

      {/* Vignettes de pages */}
      <div className="max-w-3xl mx-auto w-full px-4">
        <div className="flex gap-2.5 overflow-x-auto no-scrollbar py-2" data-testid="content-reader-thumbnails">
          {toc.map((t) => (
            <button
              key={t.number}
              onClick={() => goTo(t.number)}
              data-testid={`content-reader-thumb-${t.number}`}
              className={`relative shrink-0 w-14 h-[74px] rounded-md overflow-hidden bg-[#faf7f0] text-neutral-800 text-left transition-all ${
                t.number === current ? "ring-2 ring-amber-400 scale-[1.03]" : "opacity-55 hover:opacity-90"
              }`}
            >
              {t.image_url ? (
                <img src={t.image_url} alt={`Page ${t.number}`} className="w-full h-full object-cover object-top" loading="lazy" />
              ) : (
                <div className="p-1.5 text-[5.5px] leading-[1.35] overflow-hidden font-semibold h-full" style={{ fontFamily: "Georgia, serif" }}>
                  {t.headline}
                </div>
              )}
              <div className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[9px] font-bold text-center leading-4">
                {t.number}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Slider + compteur */}
      <div className="max-w-3xl mx-auto w-full px-6 pb-1">
        <div className="text-center text-[12px] text-white/70 mb-1.5 font-medium" data-testid="content-reader-page-indicator">
          Page {current} / {totalPages}
        </div>
        <input
          type="range"
          min={1}
          max={totalPages}
          value={current}
          onChange={(e) => goTo(parseInt(e.target.value, 10))}
          data-testid="content-reader-slider"
          className="w-full h-1 rounded-full appearance-none bg-white/15 accent-amber-400 cursor-pointer"
        />
      </div>

      {/* Toolbar bas */}
      <div className="border-t border-white/8 bg-[#0e0e12] pb-[calc(env(safe-area-inset-bottom)+8px)]">
        <div className="max-w-3xl mx-auto grid grid-cols-4 px-2 pt-2">
          {[
            { id: "toc", icon: List, label: "Sommaire", action: () => setOverlay(overlay === "toc" ? null : "toc") },
            { id: "fs", icon: Maximize, label: "Plein écran", action: toggleFullscreen },
            { id: "search", icon: Search, label: "Recherche", action: () => setOverlay(overlay === "search" ? null : "search") },
            { id: "settings", icon: Settings, label: "Paramètres", action: () => setOverlay(overlay === "settings" ? null : "settings") },
          ].map(({ id, icon: Icon, label, action }) => (
            <button
              key={id}
              onClick={action}
              data-testid={`content-reader-tool-${id}`}
              className={`flex flex-col items-center gap-1 py-1.5 rounded-xl transition-colors ${
                overlay === id ? "text-amber-300" : "text-white/60 hover:text-white"
              }`}
            >
              <Icon size={19} />
              <span className="text-[10px]">{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Zoom plein écran sur la page courante */}
      {zoomOpen && flipMode && toc[current - 1]?.image_url && (
        <PageZoomOverlay
          imageUrl={toc[current - 1].image_url}
          title={p.title}
          pageNumber={current}
          onClose={() => setZoomOpen(false)}
        />
      )}

      {/* Overlays */}
      {overlay && (
        <div className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm flex items-end" onClick={() => setOverlay(null)}>
          <div
            className="w-full max-w-3xl mx-auto bg-[#131318] rounded-t-3xl border-t border-white/10 max-h-[65vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid={`content-reader-overlay-${overlay}`}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/8">
              <div className="text-sm font-bold">
                {overlay === "toc" ? "📋 Sommaire" : overlay === "search" ? "🔍 Recherche" : "⚙️ Paramètres"}
              </div>
              <button onClick={() => setOverlay(null)} className="p-1.5 rounded-full hover:bg-white/10" data-testid="content-reader-overlay-close">
                <X size={18} />
              </button>
            </div>

            {overlay === "search" && (
              <div className="px-5 pt-3">
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Chercher un titre, une rubrique…"
                  data-testid="content-reader-search-input"
                  className="w-full px-4 py-2.5 rounded-xl bg-white/8 border border-white/10 text-sm text-white placeholder:text-white/35 outline-none focus:border-amber-400/50"
                />
              </div>
            )}

            {(overlay === "toc" || overlay === "search") && (
              <div className="overflow-y-auto px-3 py-3 space-y-1">
                {results.length === 0 && (
                  <div className="text-center text-white/40 text-sm py-6">Aucun résultat</div>
                )}
                {results.map((t) => (
                  <button
                    key={t.number}
                    onClick={() => { goTo(t.number); setQuery(""); }}
                    data-testid={`content-reader-toc-${t.number}`}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${
                      t.number === current ? "bg-amber-400/10 text-amber-300" : "hover:bg-white/5 text-white/85"
                    }`}
                  >
                    <span className="w-7 h-7 shrink-0 rounded-lg bg-white/8 flex items-center justify-center text-[11px] font-bold">
                      {t.number}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[10px] uppercase tracking-wider text-white/40">{t.section}</span>
                      <span className="block text-[13px] leading-snug line-clamp-2">{t.headline}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}

            {overlay === "settings" && (
              <div className="px-5 py-5 space-y-4">
                <div className="text-xs uppercase tracking-wider text-white/40">Taille du texte (mode lecture texte)</div>
                <div className="flex items-center gap-3">
                  {["A-", "A", "A+"].map((label, i) => (
                    <button
                      key={label}
                      onClick={() => setFontIdx(i)}
                      data-testid={`content-reader-font-${i}`}
                      className={`flex-1 py-3 rounded-xl font-bold transition-all ${
                        fontIdx === i ? "bg-amber-400 text-black" : "bg-white/8 text-white/70 hover:bg-white/15"
                      }`}
                      style={{ fontSize: `${13 + i * 3}px` }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {flipMode && (
                  <div className="text-[11px] text-white/40 leading-relaxed">
                    💡 Astuce : glissez le coin de la page ou balayez pour feuilleter le journal comme un vrai livre.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
